import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, LogOut, Settings, Shield, User } from 'lucide-react';
import sheetLogo from '../../assets/images/sheetlogo.png';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/userService';
import Profile from '../Profile/Profile';
import SheetsSectionTabs from './SheetsSectionTabs';

const SheetsHeader = ({
  activeTab = 'sheets',
  badge = 'ParrotSheets',
  badgeActions = null,
  title,
  description,
  actions = null,
  showBackButton = true,
  backPath = '/dashboard',
  backAriaLabel = 'Back to dashboard',
  onSignInClick = null
}) => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!userMenuRef.current?.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadAdminState = async () => {
      if (!user?.uid) {
        if (isActive) setIsAdmin(false);
        return;
      }

      try {
        const openUser = await userService.getUserProfile(user.uid).catch(() => null);
        if (isActive) {
          setIsAdmin(`${openUser?.role || ''}`.trim() === 'admin');
        }
      } catch {
        if (isActive) setIsAdmin(false);
      }
    };

    loadAdminState();

    return () => {
      isActive = false;
    };
  }, [user?.uid]);

  const userDisplayName = userProfile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'User';
  const userEmail = user?.email || '';
  const userInitials = `${userProfile?.displayName || user?.displayName || user?.email || ''}`
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'U';

  const openProfileModal = () => {
    setIsUserMenuOpen(false);
    setIsProfileModalOpen(true);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <>
      <header className="mb-6 overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-3 sm:px-5">
          <div className="flex items-center justify-between gap-2 lg:gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              {showBackButton ? (
                <button
                  type="button"
                  onClick={() => navigate(backPath)}
                  aria-label={backAriaLabel}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              ) : null}

              <div className="flex min-w-0 items-center">
                <div className="min-w-0">
                  <img src={sheetLogo} alt="ParrotSheets" className="h-10 max-w-[118px] object-contain sm:h-12 sm:max-w-none" />
                  <p className="hidden truncate text-xs font-medium text-slate-500 sm:block">
                    Structured sheets and workspaces
                  </p>
                </div>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 sm:gap-3 lg:justify-end">
              <SheetsSectionTabs activeTab={activeTab} isAdmin={isAdmin} />

              <div className="flex shrink-0 justify-end" ref={userMenuRef}>
              {user ? (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsUserMenuOpen((prev) => !prev)}
                    className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-2 transition hover:border-slate-300 hover:bg-slate-50"
                    aria-expanded={isUserMenuOpen}
                    aria-haspopup="menu"
                  >
                    {userProfile?.photoURL || user?.photoURL ? (
                      <img
                        src={userProfile?.photoURL || user?.photoURL}
                        alt="Profile"
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#008080]">
                        <span className="text-xs font-semibold text-white">{userInitials}</span>
                      </div>
                    )}
                    <div className="hidden min-w-0 text-left sm:block">
                      <p className="max-w-[10rem] truncate text-sm font-semibold text-slate-900">{userDisplayName}</p>
                      <p className="max-w-[10rem] truncate text-xs text-slate-500">{userEmail}</p>
                    </div>
                    <ChevronDown className={`hidden h-4 w-4 text-slate-400 transition sm:block ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 z-40 mt-2 w-56 overflow-hidden rounded-[10px] border border-slate-200 bg-white py-1 shadow-xl">
                      <div className="border-b border-slate-100 px-4 py-3">
                        <p className="truncate text-sm font-medium text-slate-900">{userDisplayName}</p>
                        <p className="truncate text-xs text-slate-500">{userEmail}</p>
                      </div>

                      <button
                        type="button"
                        onClick={openProfileModal}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                      >
                        <User className="h-4 w-4" />
                        Profile
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigate('/settings');
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                      >
                        <Settings className="h-4 w-4" />
                        Settings
                      </button>

                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => {
                          setIsUserMenuOpen(false);
                          navigate('/sheets/admin-access');
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                      >
                          <Shield className="h-4 w-4" />
                          Admin Access
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-rose-600 transition hover:bg-rose-50"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={onSignInClick}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200">
                    <User className="h-4 w-4 text-slate-600" />
                  </div>
                  <span className="hidden text-sm font-semibold text-slate-700 sm:inline">Sign In</span>
                </button>
              )}
              </div>
            </div>
          </div>
        </div>

          {(title || description || actions) ? (
            <div className="flex flex-col gap-5 px-5 py-5 xl:flex-row xl:items-end xl:justify-between">
              <div className="min-w-0 xl:flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center rounded-full bg-[#008080]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#008080]">
                    {badge}
                  </div>
                  {badgeActions}
                </div>
                {title ? (
                  <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
                ) : null}
                {description ? (
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 sm:text-base">
                    {description}
                  </p>
                ) : null}
              </div>

              {actions ? (
                <div className="flex flex-wrap gap-3 lg:justify-end xl:shrink-0 xl:flex-nowrap">
                  {actions}
                </div>
              ) : null}
            </div>
          ) : null}
      </header>

      {isProfileModalOpen && <Profile isModal onClose={() => setIsProfileModalOpen(false)} />}
    </>
  );
};

export default SheetsHeader;
